import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, CheckCircle2, XCircle, Loader2, Sparkles, RefreshCw, Copy } from "lucide-react";
import { runDiagnostics } from "../lib/diagnostics";
import { toast, Toaster } from "sonner";

export default function DiagnosticsPage() {
  const [checks, setChecks] = useState(null);
  const [loading, setLoading] = useState(true);

  async function refresh() {
    setLoading(true);
    setChecks(null);
    const result = await runDiagnostics();
    setChecks(result);
    setLoading(false);
  }

  useEffect(() => { refresh(); }, []);

  const allOk = checks && Object.values(checks).every((c) => c.ok);
  const summary = checks ? JSON.stringify(checks, null, 2) : "";

  return (
    <div className="min-h-screen bg-void text-white">
      <Toaster theme="dark" position="top-center" richColors />
      <header className="px-5 md:px-10 py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="diag-home">
          <div className="w-9 h-9 rounded-full bg-void border border-gold/40 flex items-center justify-center gold-glow">
            <Sparkles className="w-4 h-4 text-gold" strokeWidth={1.5} />
          </div>
          <div className="leading-tight">
            <div className="font-heading text-base">AIBox Global</div>
            <div className="font-numbers text-[10px] tracking-[0.3em] uppercase text-white/40">
              Diagnostics
            </div>
          </div>
        </Link>
        <Link to="/" className="text-white/50 hover:text-gold text-sm inline-flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Home
        </Link>
      </header>

      <main className="px-5 md:px-10 pb-16 max-w-2xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <div className="font-numbers text-xs tracking-[0.4em] uppercase text-gold">
            Connection Health
          </div>
          <h1 className="font-heading text-3xl md:text-4xl mt-2">
            Supabase connectivity check
          </h1>
          <p className="text-white/60 text-sm mt-3">
            This page runs four live checks against your Supabase project to identify
            why pages might be stuck loading. No data is modified.
          </p>
        </motion.div>

        <div className="mt-8 flex items-center gap-3">
          <button
            onClick={refresh}
            disabled={loading}
            className="btn-gold text-sm py-2 px-4 inline-flex items-center gap-2"
            data-testid="diag-refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            {loading ? "Running…" : "Run Again"}
          </button>
          {checks && (
            <button
              onClick={() => {
                navigator.clipboard.writeText(summary);
                toast.success("Diagnostics copied — paste this in your support request");
              }}
              className="btn-ghost text-sm py-2 px-4 inline-flex items-center gap-2"
              data-testid="diag-copy"
            >
              <Copy className="w-4 h-4" /> Copy report
            </button>
          )}
        </div>

        <div className="mt-6 rounded-2xl bg-charcoal border border-white/10 overflow-hidden">
          {loading || !checks ? (
            <div className="p-10 flex items-center justify-center gap-3 text-white/50">
              <Loader2 className="w-5 h-5 animate-spin text-gold" />
              <span className="font-numbers text-xs tracking-[0.3em] uppercase">
                Running checks…
              </span>
            </div>
          ) : (
            <ul data-testid="diag-results">
              <Row label="Env: REACT_APP_SUPABASE_URL" check={checks.env_url} />
              <Row label="Env: REACT_APP_SUPABASE_ANON_KEY" check={checks.env_key} />
              <Row label="HTTPS reachability to Supabase" check={checks.reachability} />
              <Row label="Database query: shops table" check={checks.shops_query} />
              <Row label="RPC function: next_token_number" check={checks.rpc} />
            </ul>
          )}
        </div>

        {checks && !loading && (
          <div
            className={`mt-6 rounded-2xl border p-5 ${allOk ? "border-emerald-400/30 bg-emerald-400/5" : "border-red-400/30 bg-red-400/5"}`}
            data-testid="diag-summary"
          >
            <div
              className={`font-numbers text-xs tracking-[0.3em] uppercase ${allOk ? "text-emerald-400" : "text-red-400"}`}
            >
              {allOk ? "All systems healthy" : "Issues detected"}
            </div>
            <div className="text-sm text-white/70 mt-2 leading-relaxed">
              {allOk
                ? "Your Hostinger deployment is correctly wired to Supabase. If a page still hangs, check the browser DevTools console for runtime errors."
                : "One or more checks failed. Most common causes: (1) your domain is blocked from reaching ncvfxzsnnfqtxzmjpecf.supabase.co by Hostinger's CSP or a browser extension, (2) the SQL migrations were not all applied, (3) Supabase project is paused/deleted."}
            </div>
            {checks.shops_query?.data?.length > 0 && (
              <div className="mt-4 font-numbers text-[10px] tracking-[0.25em] uppercase text-white/40">
                Shops detected: {checks.shops_query.data.map((s) => s.slug).join(" · ")}
              </div>
            )}
          </div>
        )}

        <div className="mt-10 rounded-2xl bg-charcoal/50 border border-white/5 p-5">
          <div className="font-numbers text-[10px] tracking-[0.3em] uppercase text-gold">
            Architecture Reminder
          </div>
          <div className="text-sm text-white/70 mt-2 leading-relaxed">
            AIBox Queue is a <strong className="text-white">static JAMstack app</strong>.
            There is no PHP/Node backend to deploy — the React build talks directly
            to your Supabase project (which IS the backend). When you upload to
            Hostinger, you only deploy <code className="font-numbers text-gold">build/</code>.
            All API calls go to <code className="font-numbers text-gold">{process.env.REACT_APP_SUPABASE_URL}</code>.
          </div>
        </div>
      </main>
    </div>
  );
}

function Row({ label, check }) {
  if (!check) return null;
  return (
    <li className="px-5 py-4 border-b border-white/5 last:border-b-0 flex items-center gap-3">
      {check.ok ? (
        <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" strokeWidth={1.5} />
      ) : (
        <XCircle className="w-5 h-5 text-red-400 flex-shrink-0" strokeWidth={1.5} />
      )}
      <div className="flex-1 min-w-0">
        <div className="text-sm">{label}</div>
        <div className="font-numbers text-[10px] tracking-[0.2em] text-white/40 break-all mt-0.5">
          {check.value}
        </div>
      </div>
    </li>
  );
}
